/**
 * Welcome discount popup — email capture for the first-order discount.
 *
 * Rendered for signed-out, discount-eligible visitors by
 * snippets/welcome-discount-popup.liquid. Two steps:
 *   1. email  -> the submit is handled here, never posted anywhere:
 *                checkEligibility() asks birch-profile whether the address has
 *                ordered before.
 *   2. result -> the card shows "eligible" (use this email at checkout) or
 *                "used" (discount spent — see your rewards).
 *
 * The address is RECORDED server-side: once birch-profile has answered, and
 * only when no customer has that email, it creates one with the email alone —
 * no tags or name. The popup used to post Shopify's `form 'customer'` to
 * /contact; that signup was removed on purpose. The snippet's fine print is the
 * newsletter/marketing disclosure the shopper agrees to by submitting.
 *
 * No sign-in. The first-order automatic discount is gated on a
 * amount_spent = 0 customer segment that checkout evaluates from the email
 * the shopper types there, so the old hop through Shopify's hosted sign-in
 * (6-digit code) bought nothing for the discount. Sign-in is only offered on
 * the "used" card, as the way into the rewards page.
 *
 * The lookup NEVER holds the shopper up or takes the offer away by mistake:
 * an error, a non-200 or no answer within LOOKUP_TIMEOUT_MS all mean
 * "eligible". Checkout applies the real rule either way.
 *
 * ONCE, EVER. The visitor is marked the moment the popup opens, so closing it,
 * navigating, or coming back tomorrow never shows it again. The mark is written
 * to localStorage *and* a first-party cookie: in-app browsers (Instagram,
 * Facebook, TikTok) routinely partition or wipe one of the two, and either one
 * surviving is enough to stay quiet.
 *
 * In-app browsers also get the no-<dialog> fallback below: older iOS WKWebViews
 * have no showModal(), and without it the markup would either never appear or
 * appear inline in the page. The fallback positions the same card as a fixed
 * overlay with its own scrim, focus trap and Escape handling.
 *
 * QA: append ?welcome_popup=1 to any URL to force it open (this does not mark
 * the visitor), or call window.BirchWelcomePopup.open().
 */
(function () {
  const STORAGE_KEY = 'birch:welcome-discount-popup';
  const COOKIE_NAME = 'birch_welcome_popup';
  const COOKIE_MAX_AGE = 63072000; // two years
  const EMAIL_RE = /^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/;
  // birch-profile (birch-customer-management welcome-eligibility.js), through
  // the App Proxy.
  const ELIGIBILITY_URL = '/apps/birch-profile/welcome/eligibility';
  const LOOKUP_TIMEOUT_MS = 2500;

  const popup = document.getElementById('WelcomeDiscountPopup');
  if (!popup) return;

  const supportsModal = typeof popup.showModal === 'function';
  const forced = /[?&]welcome_popup=1(&|$)/.test(window.location.search);
  const delay = Number(popup.dataset.delay || 45000);
  const scrollTrigger = Number(popup.dataset.scrollTrigger || 2500);
  const emailStep = popup.querySelector('[data-wd-step="email"]');
  const eligibleStep = popup.querySelector('[data-wd-step="eligible"]');
  const usedStep = popup.querySelector('[data-wd-step="used"]');
  const form = popup.querySelector('#WelcomeDiscountForm');
  const input = popup.querySelector('[data-wd-email-input]');
  const submit = popup.querySelector('[data-wd-submit]');
  const error = popup.querySelector('[data-wd-error]');
  const emailEchoes = popup.querySelectorAll('[data-wd-email-echo]');
  const rewardsCta = popup.querySelector('[data-wd-rewards-cta]');

  let isOpen = false;
  let submitting = false;
  let previousScrollY = 0;

  /* Seen state ----------------------------------------------------------- */

  function readState() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (stored && stored.state) return stored.state;
    } catch {
      /* storage blocked — the cookie below is the fallback */
    }
    return document.cookie.indexOf(`${COOKIE_NAME}=`) === -1 ? null : 'seen';
  }

  function writeState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
    } catch {
      /* ignore — the cookie still records it */
    }
    document.cookie = `${COOKIE_NAME}=${state}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax`;
  }

  /* Scroll lock ---------------------------------------------------------- */

  // Pinning the body (rather than only `overflow: hidden`) is what actually
  // stops the page scrolling underneath on iOS and in in-app browsers. Same
  // approach the theme's dialog component uses.
  function lockScroll() {
    previousScrollY = window.scrollY;
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${previousScrollY}px`;
  }

  function unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, previousScrollY);
  }

  /* Open / close --------------------------------------------------------- */

  function open() {
    if (isOpen) return;
    // Never stack on top of the cart drawer, quick add, search modal, etc.
    if (document.querySelector('dialog[open], details[scroll-lock][open]')) return;

    isOpen = true;
    if (!forced) writeState('seen');

    if (supportsModal) {
      popup.showModal();
    } else {
      popup.setAttribute('open', '');
      popup.classList.add('welcome-pop--fallback');
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'true');
      document.addEventListener('keydown', handleFallbackKeydown, true);
    }

    lockScroll();
    // Focusing the input here would raise the on-screen keyboard over the offer
    // before it has been read, so the close button takes first focus instead.
    popup.querySelector('[data-wd-close]')?.focus({ preventScroll: true });
    window.dispatchEvent(new CustomEvent('birch:welcome-popup:shown'));
  }

  async function close() {
    if (!isOpen) return;
    isOpen = false;

    popup.classList.add('is-closing');
    await new Promise((resolve) => {
      const done = () => resolve(undefined);
      popup.addEventListener('animationend', done, { once: true });
      setTimeout(done, 220);
    });

    if (supportsModal) {
      popup.close();
    } else {
      popup.removeAttribute('open');
      popup.classList.remove('welcome-pop--fallback');
      document.removeEventListener('keydown', handleFallbackKeydown, true);
    }

    popup.classList.remove('is-closing');
    unlockScroll();
  }

  // Without a real <dialog> there is no built-in Escape or focus containment.
  function handleFallbackKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      popup.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Submit --------------------------------------------------------------- */

  // Never posts: the lookup is the whole submission, and birch-profile records
  // the address after answering (see the header).
  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    const email = (input.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      showError('Please enter a valid email address.');
      input.focus();
      return;
    }

    hideError();
    submitting = true;
    submit.disabled = true;
    submit.textContent = 'One moment…';
    writeState('subscribed');

    showResult(email, await checkEligibility(email));
  }

  // Resolves to false ONLY when birch-profile positively answers that the
  // address has ordered before. Anything else — offline, 4xx/5xx, rate limited,
  // slow — is true: see the header for why the default is the offer.
  async function checkEligibility(email) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = setTimeout(() => controller?.abort(), LOOKUP_TIMEOUT_MS);
    try {
      const res = await fetch(ELIGIBILITY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email }),
        signal: controller?.signal,
      });
      if (!res.ok) return true;
      const data = await res.json();
      return data?.eligible !== false;
    } catch {
      return true;
    } finally {
      clearTimeout(timer);
    }
  }

  /* Result --------------------------------------------------------------- */

  // Shopify's hosted sign-in with the address prefilled, landing on the rewards
  // page — the "used" card's CTA. `return_to` must be relative.
  function rewardsLoginUrl(email) {
    return (
      `/customer_authentication/login?return_to=${encodeURIComponent('/pages/rewards')}` +
      `&login_hint=${encodeURIComponent(email)}`
    );
  }

  function showResult(email, eligible) {
    emailEchoes.forEach((el) => {
      el.textContent = email;
    });
    if (rewardsCta) rewardsCta.href = rewardsLoginUrl(email);
    // Keys the CSS that drops the "X% off your first order" headline when the
    // offer no longer applies.
    popup.dataset.wdResult = eligible ? 'eligible' : 'used';
    emailStep.hidden = true;
    eligibleStep.hidden = !eligible;
    usedStep.hidden = eligible;
    popup.scrollTop = 0;
    // The submit button that held focus is now hidden; hand focus to the
    // result's main action so keyboard and screen-reader users land on it.
    (eligible ? eligibleStep : usedStep).querySelector('.welcome-pop__cta')?.focus({ preventScroll: true });
    window.dispatchEvent(new CustomEvent('birch:welcome-popup:result', { detail: { email, eligible } }));
  }

  function showError(message) {
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
    input.setAttribute('aria-invalid', 'true');
  }

  function hideError() {
    if (!error) return;
    error.hidden = true;
    input.removeAttribute('aria-invalid');
  }

  /* Triggers ------------------------------------------------------------- */

  function arm() {
    const timer = setTimeout(fire, delay);

    function fire() {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onExitIntent);
      open();
    }

    function onScroll() {
      if (window.scrollY > scrollTrigger) fire();
    }

    // Desktop exit intent: pointer leaves through the top of the viewport.
    function onExitIntent(event) {
      if (event.clientY <= 0 && !event.relatedTarget) fire();
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    if (window.matchMedia('(hover: hover)').matches) {
      document.addEventListener('mouseout', onExitIntent);
    }
  }

  /* Wiring --------------------------------------------------------------- */

  form?.addEventListener('submit', handleSubmit);
  input?.addEventListener('input', hideError);

  // On a phone the on-screen keyboard can cover the button that submits the
  // field being typed into; nudge it back into view once the keyboard settles.
  input?.addEventListener('focus', () => {
    setTimeout(() => {
      if (popup.scrollHeight > popup.clientHeight) submit?.scrollIntoView({ block: 'nearest' });
    }, 350);
  });

  popup.querySelectorAll('[data-wd-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  // Backdrop click — the dialog fills its own box, so a click on the element
  // itself (not its children) landed outside the card.
  popup.addEventListener('click', (event) => {
    if (event.target === popup) close();
  });

  // Escape closes the native dialog; mirror the bookkeeping the close() path does.
  popup.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  window.BirchWelcomePopup = {
    open: () => {
      isOpen = false;
      open();
    },
    close,
  };

  if (forced) {
    open();
  } else if (!readState() && !window.Shopify?.designMode) {
    arm();
  }
})();
